<template lang="html">
  <div
    class="background"
    :class="{
      'with-controls' : controlsAreVisible || keyboardInputIsVisible,
      'inverted': timeIsAlmostUp || isCountingUp,
      'start-time-is-almost-up-animation': animateTimesAlmostUp,
    }"
    @animationend="disableBackgroundAnimations($event)"
    >
    <svg
      class="bg-animation"
      :class="{
        'flash' : animateFlash,
        'timer-complete' : animateTimerComplete
      }"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 100 100"
      width="100"
      height="100"
      >
  		<rect class="bg" x="0" y="0" width="100" height="100" />
      <circle class="circle c1" cx="50" cy=50 r="100" />
      <circle class="circle c2" cx="50" cy=50 r="100" />
    	<circle class="circle c3" cx="50" cy=50 r="100" />
			<circle class="circle c4" cx="50" cy=50 r="100" />
			<circle class="circle c5" cx="50" cy=50 r="100" />
			<circle class="circle c6" cx="50" cy=50 r="100" />
      <circle class="circle c7 final" cx="50" cy=50 r="120" />
    </svg>
    <slot />
  </div>
</template>

<script>
export default {
  name: 'TheBackground',

  props: {
    timeIsAlmostUp: {
      type: Boolean,
      required: false
    },
    isCountingUp: {
      type: Boolean,
      required: false
    },
    settingReducedMotion: {
      type: Boolean,
      required: false
    },
    controlsAreVisible: {
      type: Boolean,
      required: false
    },
    keyboardInputIsVisible: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      canAnimateBackgroundWarning: false,
      animateTimerComplete: false,
      animateFlash: false,
      animateTimesAlmostUp: false
    }
  },

  created() {
    this.$bus.$on('TheTimer-BackgroundFlash', $event => {
      this.animateFlash = true // Will be set to false by DOM event listener
    })
    this.$bus.$on('TheTimer-BackgroundTimerComplete', $event => {
      this.animateTimerComplete = true // Will be set to false by DOM event listener
    })
  },

  mounted(){
    setTimeout(()=>{
      this.canAnimateBackgroundWarning = true;
    }, 1000)
  },

  watch: {

    controlsAreVisible(){
      this.animateTimesAlmostUp = false // Avoid animation restart
    },

    timeIsAlmostUp(newVal){
      if (
        newVal &&
        !this.settingReducedMotion &&
        !this.animateFlash &&
        this.canAnimateBackgroundWarning
      ){
        this.animateTimesAlmostUp = true
      }
    }

  },

  methods: {

    // Remove animation classes after the animations end, so they can restart later.
    disableBackgroundAnimations(e) {
      const target = e.target.classList

      if (target[target.length-1] === 'bg'){
        this.animateFlash = false
        this.animateTimesAlmostUp = false
      }
      else if (target[target.length-1] === 'final'){
        this.animateTimesAlmostUp = false
        this.animateTimerComplete = false
        this.animateFlash = false
      }
    }
  }

}
</script>

<style scoped lang="scss">
@import '../style/abstracts/variables.scss';
@import '../style/abstracts/functions.scss';
@import '../style/abstracts/mixins.scss';

// Animations are enabled in the application, so no need to add with-motion or reduced-motion classes.

// Animation
/////////////

@keyframes flash{
	0%{
		opacity: 0;
	}
	25%{
		opacity: 1;
	}
	100%{
		opacity: 0;
	}
}
@keyframes flash-low-performance{
	0%{
		opacity: 1;
	}
	100%{
		opacity: 0;
	}
}
@keyframes grow{
	0%{
		transform: scale(0);
		opacity: 0;
	}
	1%{
		opacity: 1;
	}
	90%{
		opacity: 1;
	}
	100%{
		transform: scale(2);
		opacity: 0;
	}
}
@keyframes invert{
  0%{
    opacity: 0;
  }
  20%{
    opacity: 1;
  }
  40%{
    opacity: 0.1;
  }
  60%{
    opacity: 1;
  }
  80%{
    opacity: 0.1;
  }
  100%{
    opacity: 1;
  }
}
// Style
////////

.background {
  background-color: $black;
  transition: background-color $duration-very-short+ms ease-out;

  &.with-controls {
    // background-color: $color-very-dark;
  }
  &:not(.start-time-is-almost-up-animation).inverted{
    background: $color-primary;
    transition: none;
  }
}
.bg-animation{
  position: absolute;
  top: 0;
  right: 0;
	width: 100vw;
	height: 100vh;

  // Activate hardware acceleration
  transform: translate-z(0);
  backface-visibility: hidden;
  perspective: 1000;

}
.bg{
	fill: $color-primary;
	opacity: 0;

  .inverted &{
    fill: $white;
  }
  .start-time-is-almost-up-animation &{
    fill: $color-primary;
    animation: invert 2000ms 0s linear;
  }
  .timer-complete &{
    animation: flash 1s 0s ease-in;

    .firefox &{
      animation: flash-low-performance 1s 0s ease-out;
    }
  }
  .flash &{
    animation: flash .6s 0s linear;

    .firefox &{
      animation: flash-low-performance .6s 0s linear;
    }
  }
}
.circle{
	transform-origin: center;
	opacity: 0;

  .internet-explorer &{
    display: none;
  }
}

// Dotted circle outlines
.c1,
.c2,
.c3{
  fill: transparent;
  stroke-linecap: round;
}

// Burst of rings
.c4,
.c5,
.c6{
	fill: transparent;
	stroke: $color-primary;
	stroke-width: 4;

  .inverted &{
    stroke: $black;
  }
}
.c1{
  stroke: $color-primary;
  stroke-width: 4;
  stroke-dasharray: 0 15;

  .timer-complete &{
    animation: grow 1.3s 0s ease-in;
  }
  .inverted &{
    stroke: $black;
  }
}
.c2{
  stroke: $white;
  stroke-width: 5;
  stroke-dasharray: 0 10;

  .timer-complete &{
    animation: grow 0.8s 0.2s ease-in;
  }
}
.c3{
  stroke-width: 5;
  stroke-dasharray: 0 15;
  stroke: $color-primary;

  .timer-complete &{
    animation: grow 1.6s 1.2s ease-in;
  }
  .inverted &{
    stroke: $black;
  }
}
.c4{

  .timer-complete &{
    animation: grow 1.8s 0.3s ease-in;
  }
}
.c5{

  .timer-complete &{
    animation: grow 1.8s 0.5s ease-in;
  }
}
.c6{

  .timer-complete &{
    animation: grow 1.8s 0.7s ease-in;
  }
}
.c7{
  fill: transparent;
  stroke: $white;
  stroke-width: 160;

  .timer-complete &{
    animation: grow 0.8s 1.7s ease-in;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/TheBackground.vue